/**
 * useEditorialAi — unified hook for AI writing assistance.
 * Replaces useAreaAiAssist and usePlaceAiAssist with a single shared invoke layer.
 */
import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// ─── Shared types ───────────────────────────────

export type EditorialAiAction = "generate" | "rewrite" | "shorten";
export type EditorialAiLength = "short" | "medium" | "long";
export type EditorialAiLengthProfile = "summary" | "body";

export interface EditorialAiOptions {
  action?: EditorialAiAction;
  length?: EditorialAiLength;
  lengthProfile?: EditorialAiLengthProfile;
}

export type EditorialAiTask =
  | "area_short_description_draft"
  | "area_long_description_draft"
  | "rewrite_in_farmly_tone"
  | "place_summary_suggest"
  | "place_experience_suggest"
  | "activity_description_improve";

export interface EditorialAiContext {
  // Common
  name: string;
  municipality?: string;
  landskap?: string;
  tags?: string[];
  seasons?: string[];
  currentText?: string;
  fieldType?: string;
  // Area-specific
  parentAreaName?: string;
  areaType?: string;
  descriptionShort?: string;
  nearbyPlaceNames?: string[];
  // Place-specific
  intentLabels?: string[];
  // Rewrite
  text?: string;
  // Activity-specific
  title?: string;
  description?: string;
  hasAnimals?: boolean;
  hasFarmShop?: boolean;
  hasFoodProduction?: boolean;
  hasNature?: boolean;
}

export interface UseEditorialAiReturn {
  generate: (
    task: EditorialAiTask,
    context: EditorialAiContext,
    options?: EditorialAiOptions
  ) => Promise<string | null>;
  loading: boolean;
}

export function useEditorialAi(): UseEditorialAiReturn {
  const [loading, setLoading] = useState(false);

  const generate = useCallback(
    async (
      task: EditorialAiTask,
      context: EditorialAiContext,
      options?: EditorialAiOptions
    ): Promise<string | null> => {
      setLoading(true);
      try {
        const body: Record<string, unknown> = { task, context };
        if (options && Object.keys(options).length > 0) {
          body.options = options;
        }

        const { data, error } = await supabase.functions.invoke("ai-generate", {
          body,
        });

        if (error) throw error;

        if (data?.error) {
          const isQuota = data.reason === "insufficient_quota";
          toast.error(
            isQuota ? "AI-kvot saknas" : "AI-förslag misslyckades",
            {
              description: isQuota
                ? data.admin_action ?? "Kontrollera OpenAI billing och credits."
                : data.error,
            }
          );
          return null;
        }

        const text = data?.result?.text ?? data?.result?.vibe;
        if (!text) {
          toast.error("AI-förslag misslyckades", {
            description: "Inget förslag returnerades.",
          });
          return null;
        }

        return text;
      } catch (err) {
        console.error("[EditorialAi] error:", err);
        toast.error("AI-förslag misslyckades", {
          description: "Kunde inte nå AI-tjänsten just nu.",
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { generate, loading };
}
